<template>
  <v-container class="pa-0 tk-page">
    <div
      class="tk-shell"
      :class="{ 'tk-shell--detail': mobilePane === 'detail' }"
    >
      <section class="tk-pane tk-pane--list">
        <TicketList
          ref="ticketList"
          @isFilterDrawerVisible="isFilterDrawerVisible"
          @isConversationDrawerVisible="isConversationDrawerVisible"
          @openTicketConversation="openTicketConversation"
          :selectedDepartmentIdFilter="selectedDepartmentIdFilter"
          :selectedStatusId="selectedStatus"
          :selectedFromDate="selectedFromDate"
          :selectedToDate="selectedToDate"
          :selectedUserId="selectedUserId"
          :selectedCustomerId="selectedCustomerId"
          :isFiltersApply="isFiltersApply"
          :isFiltersRemove="isFiltersRemove"
          @ticketSelection="ticketSelectionHandler"
          @onSelectTicket="onSelectTicketHandler"
          @createNewTicket="createNewTicketHandler"
          :newTicket="newTicket"
          :closedTicketId="closedTicketId"
          :statusRefreshKey="statusRefreshKey"
          @onToggleTicketSelection="onToggleTicketSelection"
        />
      </section>
      <section class="tk-pane tk-pane--detail">
        <button
          type="button"
          class="tk-back"
          :aria-label="$t('back')"
          @click="mobilePane = 'list'"
        >
          <v-icon>{{ $vuetify.rtl ? "mdi-arrow-right" : "mdi-arrow-left" }}</v-icon>
        </button>
        <TicketMessage
          :isTicketSelected="isTicketSelected"
          :selectedTicket="selectedTicket"
          :isCreateNewTicket="isCreateNewTicket"
          @addTickets="addTicketsHandler"
          @closeTicket="closeTicket"
          @ticketStatusChanged="onTicketStatusChanged"
          @liveTicketMessage="onLiveTicketMessage"
          @openTicketConversation="openTicketConversation"
          :toggleSelectionTicket="toggleSelectionTicket"
        />
      </section>
    </div>

    <v-navigation-drawer
      v-if="isSupportAbr"
      v-model="conversationDrawerVisibility"
      fixed
      temporary
      left
      :width="conversationDrawerWidth"
      elevation="0"
      class="ticket-conversation-drawer"
      :class="{
        'ticket-conversation-drawer--mobile': isConversationMobileLayout,
        'ticket-conversation-drawer--mobile-detail':
          isConversationMobileLayout && conversationMobilePane === 'detail',
      }"
    >
      <div class="ticket-conversation-drawer-inner">
        <div class="ticket-conversation-drawer-header">
          <v-card-text class="pa-0">{{ $t("conversations") }}</v-card-text>
          <v-icon style="cursor: pointer" @click="conversationDrawerVisibility = false">
            mdi-close
          </v-icon>
        </div>
        <div class="ticket-conversation-drawer-body">
          <Conversation
            v-if="conversationDrawerVisibility"
            :ref-type="conversationFocusTicketId != null ? 'ticket' : null"
            :ref-id="conversationFocusTicketId"
            :title="conversationFocusTicketTitle"
            @mobile-pane="onConversationMobilePane"
          />
        </div>
      </div>
    </v-navigation-drawer>

    <v-navigation-drawer
      ref="filterDrawer"
      :value="filterDrawerVisibility"
      :absolute="!isMobileView"
      :fixed="isMobileView"
      :bottom="isMobileView"
      temporary
      :width="isMobileView ? '100%' : 380"
      elevation="0"
      class="ticket-drawer tk-filter"
      :class="{ 'tk-filter--sheet': isMobileView }"
      @input="onFilterDrawerInput"
    >
      <div class="tk-filter__inner">
        <span v-if="isMobileView" class="tk-filter__handle" />
        <div class="tk-filter__head">
          <div class="tk-filter__title">
            <span class="tk-filter__icon">
              <my-icon
                :name="'filter-icon'"
                :width="16"
                :height="15"
                :stroke="'#0066FF'"
              />
            </span>
            <span>{{ $t("filter") }}</span>
          </div>
          <button
            type="button"
            class="tk-filter__close"
            :aria-label="$t('close')"
            @click="filterDrawerVisibility = false"
          >
            <v-icon small>mdi-close</v-icon>
          </button>
        </div>

        <div class="tk-filter__body">
          <div class="tk-filter__section">
            <div class="tk-filter__label">{{ $t("departments") }}</div>
            <autocomplete
              v-model="department"
              :title="$t('departments')"
              type="departments"
              :value="department"
              class="flex-fill"
              @clear="clearFilters('department')"
            />
          </div>
          <div v-if="isSupportAbr" class="tk-filter__section">
            <div class="tk-filter__label">{{ $t("user") }}</div>
            <FlexibleAutocomplete
              v-model="user"
              title="user"
              type="user"
              :multiple="false"
              :fetchData="fetchUsersApi"
              class="flex-fill"
            />
          </div>
          <div v-if="isSupportAbr" class="tk-filter__section">
            <div class="tk-filter__label">{{ $t("customer") }}</div>
            <FlexibleAutocomplete
              v-model="customer"
              title="customer"
              type="customer"
              :multiple="false"
              :fetchData="fetchCustomersApi"
              class="flex-fill"
            />
          </div>
          <div class="tk-filter__section">
            <div class="tk-filter__label">{{ $t("choose date") }}</div>
            <div class="tk-filter__dates">
              <div class="tk-filter__date">
                <v-text-field
                  id="fromDate"
                  v-model="selectedFromDate"
                  :label="$t('fromDate')"
                  append-icon="mdi-calendar-blank-outline"
                  outlined
                  dense
                  hide-details
                  clearable
                />
                <date-picker
                  v-model="selectedFromDate"
                  element="fromDate"
                  append-to="body"
                  @open="datePickerOpen = true"
                  @close="onDatePickerClose"
                  format="jYYYY/jMM/jDD"
                />
              </div>
              <div class="tk-filter__date">
                <v-text-field
                  id="toDate"
                  v-model="selectedToDate"
                  :label="$t('toDate')"
                  append-icon="mdi-calendar-blank-outline"
                  outlined
                  dense
                  hide-details
                  clearable
                />
                <date-picker
                  v-model="selectedToDate"
                  element="toDate"
                  append-to="body"
                  @open="datePickerOpen = true"
                  @close="onDatePickerClose"
                  format="jYYYY/jMM/jDD"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="tk-filter__foot">
          <v-btn depressed color="primary" class="tk-filter__apply" @click="applyFilter">
            {{ $t("apply filters") }}
          </v-btn>
          <v-btn text class="tk-filter__clear" @click="removeFilter">
            {{ $t("delete filters") }}
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>
  </v-container>
</template>

<script>
import TicketList from "@/views/dashboard/pages/ticket/partial/TicketList.vue";
import TicketMessage from "@/views/dashboard/pages/ticket/partial/TicketMessage.vue";
import FlexibleAutocomplete from "@/views/dashboard/components/general/patial/FlexibleAutocomplete.vue";
import Conversation from "@/views/dashboard/components/general/conversation/Conversation.vue";

export default {
  name: "App",
  data() {
    return {
      filterDrawerVisibility: false,
      conversationDrawerVisibility: false,
      conversationFocusTicketId: null,
      conversationFocusTicketTitle: "",
      status: null,
      departments: null,
      selectedStatus: null,
      isFiltersApply: false,
      isFiltersRemove: false,
      isTicketSelected: false,
      selectedTicket: null,
      isCreateNewTicket: false,
      newTicket: null,
      selectedFromDate: null,
      selectedToDate: null,
      closedTicketId: null,
      statusRefreshKey: 0,
      department: null,
      selectedDepartmentIdFilter: null,
      user: null,
      customer: null,
      selectedUserId: null,
      selectedCustomerId: null,
      toggleSelectionTicket: false,
      mobilePane: "list",
      conversationMobilePane: "list",
      datePickerOpen: false,
      datePickerClosedAt: 0,
    };
  },
  components: {
    TicketList,
    TicketMessage,
    FlexibleAutocomplete,
    Conversation,
  },
  computed: {
    isMobileView() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    isConversationMobileLayout() {
      return (
        this.$vuetify &&
        this.$vuetify.breakpoint &&
        this.$vuetify.breakpoint.width < 800
      );
    },
    conversationDrawerWidth() {
      return this.isConversationMobileLayout ? "100%" : 820;
    },
    selectedTicketId() {
      return this.selectedTicket && this.selectedTicket.id != null
        ? this.selectedTicket.id
        : null;
    },
    isSupportAbr() {
      try {
        const token = JSON.parse(localStorage.getItem("token") || "{}");
        return (
          Number(
            token &&
              token.init &&
              token.init.appBarsOpacity &&
              token.init.appBarsOpacity.abr
          ) === 83
        );
      } catch (e) {
        return false;
      }
    },
  },
  methods: {
    mapAutocompleteItems(list) {
      return (Array.isArray(list) ? list : []).map(function (item) {
        if (!item || item.name) {
          return item;
        }
        const name =
          item.value ||
          item.name ||
          [item.firstname, item.lastname].filter(Boolean).join(" ").trim() ||
          item.title ||
          item.full_name ||
          item.fullname ||
          String(item.id);
        return Object.assign({}, item, { name });
      });
    },
    resolveList(response) {
      let list = response && response.data ? response.data : [];
      if (!Array.isArray(list) && list && Array.isArray(list.data)) {
        list = list.data;
      }
      return Array.isArray(list) ? list : [];
    },
    resolveSelectedId(val) {
      const item = Array.isArray(val) ? val[0] : val;
      return item && item.abr != null ? item.abr : null;
    },
    resolveSelectedValue(val) {
      const item = Array.isArray(val) ? val[0] : val;
      return item && item.id != null ? item.id : null;
    },
    fetchUsersApi(payload) {
      var app = this;
      let params = {
        page: payload.page,
        search: payload.search || "",
        options: JSON.stringify({
          itemsPerPage: 10,
        }),
      };
      return app.$axios
        .get(app.$endpoint + "reports/get-main-users", { params })
        .then(function (response) {
          let list = app.resolveList(response);
          return {
            items: app.mapAutocompleteItems(list),
            hasMore: list.length === 10,
          };
        });
    },
    fetchCustomersApi(payload) {
      var app = this;
      let params = {
        page: payload.page,
        search: payload.search || "",
        options: JSON.stringify({
          itemsPerPage: 10,
        }),
      };
      return app.$axios
        .get(app.$endpoint + "reports/customer-abr-configs", { params })
        .then(function (response) {
          let list = app.resolveList(response);
          return {
            items: app.mapAutocompleteItems(list),
            hasMore: list.length === 10,
          };
        });
    },
    // the calendar renders on <body>, so clicks in it look like "outside" clicks to the drawer
    onFilterDrawerInput(val) {
      if (!val && (this.datePickerOpen || Date.now() - this.datePickerClosedAt < 400)) {
        if (this.$refs.filterDrawer) {
          this.$refs.filterDrawer.isActive = true;
        }
        return;
      }
      this.filterDrawerVisibility = val;
    },
    onDatePickerClose() {
      this.datePickerOpen = false;
      this.datePickerClosedAt = Date.now();
    },
    isFilterDrawerVisible() {
      this.filterDrawerVisibility = !this.filterDrawerVisibility;
    },
    isConversationDrawerVisible() {
      if (!this.isSupportAbr) {
        return;
      }
      // Global entry: show all conversations, do not focus selected ticket
      this.conversationFocusTicketId = null;
      this.conversationFocusTicketTitle = "";
      this.conversationMobilePane = "list";
      this.conversationDrawerVisibility = !this.conversationDrawerVisibility;
    },
    openTicketConversation(payload) {
      if (!this.isSupportAbr) {
        return;
      }
      const ticket = payload || {};
      // Per-ticket entry: open/create chat for this ticket only
      this.conversationFocusTicketId =
        ticket.id != null ? ticket.id : this.selectedTicketId;
      this.conversationFocusTicketTitle =
        ticket.title || (this.selectedTicket && this.selectedTicket.title) || "";
      this.conversationMobilePane = "list";
      this.conversationDrawerVisibility = true;
    },
    onConversationMobilePane(pane) {
      this.conversationMobilePane = pane === "detail" ? "detail" : "list";
    },
    applyFilter() {
      this.isFiltersApply = !this.isFiltersApply;
      this.filterDrawerVisibility = false;
    },
    removeFilter() {
      this.isFiltersRemove = !this.isFiltersRemove;
      this.department = null;
      this.selectedDepartmentIdFilter = null;
      this.status = null;
      this.selectedStatus = null;
      this.user = null;
      this.selectedUserId = null;
      this.customer = null;
      this.selectedCustomerId = null;
      this.selectedFromDate = null;
      this.selectedToDate = null;
      this.filterDrawerVisibility = false;
    },
    ticketSelectionHandler(value) {
      this.isTicketSelected = value;
    },
    onToggleTicketSelection(val) {
      this.toggleSelectionTicket = val;
    },
    onSelectTicketHandler(value) {
      this.selectedTicket = value;
      this.mobilePane = "detail";
    },
    createNewTicketHandler() {
      this.selectedTicket = null;
      this.isCreateNewTicket = !this.isCreateNewTicket;
      this.mobilePane = "detail";
    },
    addTicketsHandler(value) {
      this.newTicket = value;
    },
    onTicketStatusChanged() {
      this.statusRefreshKey += 1;
    },
    onLiveTicketMessage(payload) {
      if (this.$refs.ticketList && this.$refs.ticketList.prependLiveTicket) {
        this.$refs.ticketList.prependLiveTicket(payload);
      }
    },
    closeTicket(value) {
      this.closedTicketId = value;
    },
    clearFilters(value) {
      if (value == "department") {
        this.selectedDepartmentIdFilter = null;
      }
      if (value == "status") {
        this.selectedStatus = null;
      }
      if (value == "user") {
        this.selectedUserId = null;
      }
      if (value == "customer") {
        this.selectedCustomerId = null;
      }
    },
  },
  watch: {
    department(val) {
      if (val) {
        this.selectedDepartmentIdFilter = val.id;
      }
    },
    status(val) {
      if (val) {
        this.selectedStatus = val.id;
      }
    },
    user(val) {
      this.selectedUserId = this.resolveSelectedValue(val);
    },
    customer(val) {
      this.selectedCustomerId = this.resolveSelectedId(val);
    },
    conversationDrawerVisibility(val) {
      if (!val) {
        this.conversationMobilePane = "list";
      }
    },
  },
};
</script>

<style>
/* ---------- filter panel (side panel on web, bottom sheet on mobile) ---------- */
.tk-filter.v-navigation-drawer {
  z-index: 9 !important;
}
.tk-filter.ticket-drawer.v-navigation-drawer {
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.16) !important;
}
.tk-filter .v-navigation-drawer__content {
  overflow: hidden;
}
.tk-filter__inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.tk-filter__handle {
  align-self: center;
  width: 40px;
  height: 4px;
  margin: 10px 0 2px;
  border-radius: 4px;
  background: #cbd5e1;
  flex: 0 0 auto;
}
.tk-filter__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #e6eaf0;
  flex: 0 0 auto;
}
.tk-filter__title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
}
.tk-filter__icon {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  background: #eef4ff;
}
.tk-filter__close {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  color: #64748b;
}
.tk-filter__close:hover {
  background: #f4f7fb;
}
.tk-filter__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.tk-filter__label {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}
.tk-filter__section .v-input {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
.tk-filter__section .v-text-field__details {
  display: none;
}
.tk-filter__section .v-input__slot {
  border-radius: 4px !important;
}
.tk-filter__dates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.tk-filter__date {
  position: relative;
  min-width: 0;
}
/* the picker opens from the field itself; hide its extra icon button */
.tk-filter__date .vpd-icon-btn {
  display: none !important;
}
.tk-filter__date .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center;
}
.tk-filter__date .v-input__append-inner .v-icon {
  font-size: 18px !important;
  color: #64748b !important;
}
.tk-filter__date .v-input__slot {
  padding: 0 12px !important;
}
.tk-filter__foot {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 12px 20px 16px;
  border-top: 1px solid #e6eaf0;
  background: #fff;
}
.tk-filter__foot .v-btn {
  height: 40px !important;
  border-radius: 4px !important;
  letter-spacing: 0 !important;
  font-weight: 700 !important;
  text-transform: none !important;
}
.tk-filter__apply {
  flex: 1 1 auto;
}
.tk-filter__clear {
  flex: 0 0 auto;
  color: #64748b !important;
}
/* mobile bottom sheet */
.tk-filter--sheet.v-navigation-drawer {
  top: auto !important;
  bottom: 0 !important;
  height: auto !important;
  max-height: 88vh !important;
  border-radius: 16px 16px 0 0;
}
.tk-filter--sheet .tk-filter__inner {
  max-height: 88vh;
}
.tk-filter--sheet .tk-filter__head {
  padding: 8px 16px 12px;
}
.tk-filter--sheet .tk-filter__body {
  padding: 14px 16px;
}
.tk-filter--sheet .tk-filter__foot {
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
}
.theme--dark .tk-filter__head,
.theme--dark .tk-filter__foot {
  border-color: #333;
  background: #1e1e1e;
}
.theme--dark .tk-filter__title {
  color: #eee;
}
.theme--dark .tk-filter__icon {
  background: rgba(0, 102, 255, 0.18);
}

/* ---------- page shell ---------- */
.tk-page {
  max-width: 100% !important;
  height: calc(100vh - 98px);
  max-height: calc(100vh - 98px);
  min-height: 0;
}
.tk-shell {
  display: grid;
  grid-template-columns: minmax(320px, 380px) minmax(0, 1fr);
  height: 100%;
  max-height: 100%;
  min-height: 0;
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 4px;
  overflow: hidden;
}
.tk-pane {
  position: relative;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}
.tk-pane--list {
  padding: 16px 14px 0;
  border-inline-end: 1px solid #e6eaf0;
}
.tk-pane--detail {
  background: #f7f9fc;
}
.tk-pane > .wrapper-ticket-message {
  flex: 1 1 auto;
  min-height: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
}
.tk-back {
  display: none;
}
.theme--dark .tk-shell,
.theme--dark .tk-pane {
  background: #1e1e1e;
  border-color: #333;
}
.theme--dark .tk-pane--detail {
  background: #181818;
}

/* ---------- mobile ---------- */
@media (max-width: 959px) {
  .tk-page {
    height: auto;
    max-height: none;
  }
  /* list fills between app bar and viewport bottom (global footer is dashboard-only) */
  .tk-shell {
    display: block;
    position: fixed;
    top: 52px;
    left: 0;
    right: 0;
    bottom: env(safe-area-inset-bottom, 0px);
    height: auto;
    max-height: none;
    min-height: 0;
    border: 0;
    border-radius: 0;
    z-index: 2;
  }
  .tk-pane {
    height: 100%;
  }
  .tk-pane--list {
    padding: 12px 12px 0;
    border: 0;
  }
  .tk-shell .tk-pane--detail,
  .tk-shell--detail .tk-pane--list {
    display: none;
  }
  /* detail shell above list chrome; starts below the app bar */
  .tk-shell--detail {
    z-index: 8;
  }
  /* conversation opens full screen like a chat app */
  .tk-shell--detail .tk-pane--detail {
    display: flex;
    position: fixed;
    top: 52px; /* keep the app bar visible */
    right: 0;
    bottom: 0;
    left: 0;
    height: auto;
    z-index: 7;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
  .tk-back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 10px;
    inset-inline-start: 8px;
    z-index: 3;
    width: 36px;
    height: 36px;
    border-radius: 4px;
    color: #0f172a;
  }
  .tk-back:active {
    background: rgba(15, 23, 42, 0.06);
  }
  .tk-shell .tk-pane--detail .ticket-header-bar,
  .tk-shell .tk-pane--detail .ticket-create-fields {
    padding-inline-start: 52px !important;
  }
  .tk-pane--detail .wrapper-view .layer {
    padding-top: 56px;
  }
  .theme--dark .tk-back {
    color: #eee;
  }
}
.ticket-conversation-drawer,
.ticket-conversation-drawer .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}
.ticket-conversation-drawer.v-navigation-drawer {
  box-shadow: none !important;
  z-index: 10 !important;
  height: 100% !important;
  top: 0 !important;
  max-height: 100% !important;
  border-right: 1px solid #e5e5e5;
}
.ticket-conversation-drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}
.ticket-conversation-drawer-header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}
.ticket-conversation-drawer-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
@media (max-width: 799px) {
  .ticket-conversation-drawer.v-navigation-drawer {
    z-index: 12 !important;
    width: 100% !important;
    max-width: 100% !important;
    border-right: 0;
  }
  .ticket-conversation-drawer-header {
    padding: 12px 14px;
    padding-top: calc(12px + env(safe-area-inset-top, 0px));
  }
  .ticket-conversation-drawer--mobile-detail .ticket-conversation-drawer-header {
    display: none;
  }
}
.ticket-drawer,
.ticket-drawer .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}
.ticket-drawer.v-navigation-drawer {
  box-shadow: none !important;
}
/** global for ticket **/
.v-card .v-divider {
  border-color: rgba(192, 192, 192, 0.765) !important;
}
.v-divider--vertical.v-divider--inset {
  margin-top: 0 !important;
}
.v-card--link:before {
  background: transparent !important;
}
.v-menu__content::-webkit-scrollbar {
  width: 4.5px;
  background-color: silver !important;
}
.v-menu__content::-webkit-scrollbar-track {
  background-color: rgba(189, 189, 189, 0.769);
  border: none !important;
}
.v-menu__content::-webkit-scrollbar-thumb {
  background-color: rgba(105, 101, 101, 0.767) !important;
  border: none !important;
}
/* .v-navigation-drawer .v-icon.v-icon {
    font-size: 20px !important;
} */
</style>
